/**
 * Transport quality monitoring hook for WebSocket audio/transcription sessions.
 *
 * Tracks transcript delivery jitter, WebSocket backpressure, and reconnection
 * timing to quantify TCP head-of-line blocking effects. Data is emitted as
 * clickstream events for Grafana visualization.
 *
 * Usage in SessionRightPanel.js:
 *
 *   const transportQuality = useTransportQuality({
 *     wsRef,
 *     recording,
 *     sessionId: session_id,
 *     patientId: patient_id,
 *     metrics,
 *   });
 *
 *   // In handleWebSocketMessage, when transcript arrives:
 *   transportQuality.onTranscriptReceived();
 *
 *   // In attemptReconnect, when reconnection completes:
 *   transportQuality.onReconnectComplete(attempt);
 *
 *   // In attemptReconnect, when reconnection starts:
 *   transportQuality.onReconnectStart();
 */
import { useCallback, useEffect, useRef } from 'react';

// Transcript gap thresholds
const EXPECTED_TRANSCRIPT_INTERVAL_MS = 1000; // ~1s between partial transcripts
const STALL_MULTIPLIER = 2; // gap > 2x expected = stall
const BURST_WINDOW_MS = 200; // messages within 200ms of each other = burst

// Backpressure sampling
const BACKPRESSURE_CHECK_INTERVAL_MS = 2000;
const BACKPRESSURE_THRESHOLD_BYTES = 8192; // 8KB queued = backpressure

// Stats emission interval
const STATS_EMIT_INTERVAL_MS = 30000; // every 30s

export const useTransportQuality = ({
  wsRef,
  recording,
  sessionId,
  patientId,
  metrics,
}) => {
  // Transcript delivery tracking
  const lastTranscriptAtRef = useRef(null);
  const transcriptGapsRef = useRef([]);
  const stallCountRef = useRef(0);
  const burstCountRef = useRef(0);
  const recentArrivalsRef = useRef([]);

  // Reconnection tracking
  const reconnectStartRef = useRef(null);
  const lastAudioSentRef = useRef(null);

  // Timer refs
  const backpressureTimerRef = useRef(null);
  const statsTimerRef = useRef(null);
  const elapsedRef = useRef(null);

  /**
   * Call when a transcript message (partial or complete) arrives via WS.
   */
  const onTranscriptReceived = useCallback(() => {
    const now = performance.now();

    if (lastTranscriptAtRef.current !== null) {
      const gap = now - lastTranscriptAtRef.current;
      transcriptGapsRef.current.push(gap);

      // Stall detection: gap > 2x expected
      if (gap > EXPECTED_TRANSCRIPT_INTERVAL_MS * STALL_MULTIPLIER) {
        stallCountRef.current += 1;
      }

      // Burst detection: check if multiple messages arrived within burst window
      recentArrivalsRef.current.push(now);
      const cutoff = now - BURST_WINDOW_MS;
      recentArrivalsRef.current = recentArrivalsRef.current.filter(
        (t) => t >= cutoff
      );

      if (recentArrivalsRef.current.length >= 3) {
        burstCountRef.current += 1;

        // Emit stall event with burst context
        if (metrics) {
          metrics.add(
            metrics.transcriptStallEvent({
              session_id: sessionId,
              patient_id: patientId,
              gap_ms: Math.round(gap),
              expected_ms: EXPECTED_TRANSCRIPT_INTERVAL_MS,
              buffered_count: recentArrivalsRef.current.length,
            })
          );
        }

        recentArrivalsRef.current = [];
      }
    }

    lastTranscriptAtRef.current = now;
  }, [metrics, sessionId, patientId]);

  /**
   * Call when audio is sent via WebSocket (to track audio gap during reconnect).
   */
  const onAudioSent = useCallback(() => {
    lastAudioSentRef.current = performance.now();
  }, []);

  /**
   * Call when reconnection attempt starts.
   */
  const onReconnectStart = useCallback(() => {
    reconnectStartRef.current = performance.now();
  }, []);

  /**
   * Call when reconnection succeeds.
   */
  const onReconnectComplete = useCallback(
    (attemptNumber) => {
      if (!metrics) return;

      const now = performance.now();
      const duration =
        reconnectStartRef.current !== null
          ? now - reconnectStartRef.current
          : 0;
      const audioGap =
        lastAudioSentRef.current !== null
          ? now - lastAudioSentRef.current
          : 0;

      metrics.add(
        metrics.wsReconnectDurationEvent({
          session_id: sessionId,
          patient_id: patientId,
          duration_ms: Math.round(duration),
          attempt_number: attemptNumber,
          audio_gap_ms: Math.round(audioGap),
        })
      );

      reconnectStartRef.current = null;
    },
    [metrics, sessionId, patientId]
  );

  // Backpressure monitoring: sample ws.bufferedAmount periodically
  useEffect(() => {
    if (!recording || !wsRef?.current || !metrics) return;

    elapsedRef.current = performance.now();

    backpressureTimerRef.current = setInterval(() => {
      const ws = wsRef.current;
      if (!ws || ws.readyState !== WebSocket.OPEN) return;

      if (ws.bufferedAmount > BACKPRESSURE_THRESHOLD_BYTES) {
        const elapsed = (performance.now() - elapsedRef.current) / 1000;
        metrics.add(
          metrics.wsBackpressureEvent({
            session_id: sessionId,
            patient_id: patientId,
            buffered_bytes: ws.bufferedAmount,
            recording_duration_seconds: Math.round(elapsed),
          })
        );
      }
    }, BACKPRESSURE_CHECK_INTERVAL_MS);

    return () => {
      if (backpressureTimerRef.current) {
        clearInterval(backpressureTimerRef.current);
        backpressureTimerRef.current = null;
      }
    };
  }, [recording, wsRef, metrics, sessionId, patientId]);

  // Periodic stats emission: aggregate transcript delivery quality every 30s
  useEffect(() => {
    if (!recording || !metrics) return;

    statsTimerRef.current = setInterval(() => {
      const gaps = transcriptGapsRef.current;
      if (gaps.length === 0) return;

      const mean = gaps.reduce((a, b) => a + b, 0) / gaps.length;
      const max = Math.max(...gaps);

      metrics.add(
        metrics.transcriptDeliveryStatsEvent({
          session_id: sessionId,
          patient_id: patientId,
          window_seconds: STATS_EMIT_INTERVAL_MS / 1000,
          segments_received: gaps.length,
          mean_gap_ms: Math.round(mean),
          max_gap_ms: Math.round(max),
          stall_count: stallCountRef.current,
          burst_count: burstCountRef.current,
        })
      );

      // Reset for next window
      transcriptGapsRef.current = [];
      stallCountRef.current = 0;
      burstCountRef.current = 0;
    }, STATS_EMIT_INTERVAL_MS);

    return () => {
      if (statsTimerRef.current) {
        clearInterval(statsTimerRef.current);
        statsTimerRef.current = null;
      }
    };
  }, [recording, metrics, sessionId, patientId]);

  // Reset state when recording stops
  useEffect(() => {
    if (!recording) {
      lastTranscriptAtRef.current = null;
      transcriptGapsRef.current = [];
      stallCountRef.current = 0;
      burstCountRef.current = 0;
      recentArrivalsRef.current = [];
      reconnectStartRef.current = null;
      lastAudioSentRef.current = null;
    }
  }, [recording]);

  return {
    onTranscriptReceived,
    onAudioSent,
    onReconnectStart,
    onReconnectComplete,
  };
};
